/**
 * use-display-mode — parse ?mode=tv from the URL once at mount time.
 *
 * Design contract (glance-grammar-spec §2.4):
 *   - Triggered by URL param `?mode=tv`.
 *   - Read ONCE at boot; no reactivity needed (a TV display doesn't change URLs).
 *   - Returns 'tv' | 'default'.
 *
 * The parsing is extracted as a pure function so unit tests cover it without
 * touching the DOM or React.
 */
import { useState } from 'react';

/** The two display modes Briefpane currently supports. */
export type DisplayMode = 'default' | 'tv';

/**
 * Parse a search-string (e.g. "?mode=tv") into a DisplayMode.
 *
 * Pure function — no side effects, safe to unit test directly.
 *
 * @param search - window.location.search or any query string to parse.
 * @returns 'tv' if mode=tv is present (case-insensitive), 'default' otherwise.
 */
export function parseDisplayMode(search: string): DisplayMode {
  try {
    const params = new URLSearchParams(search);
    const raw = params.get('mode');
    if (raw !== null && raw.toLowerCase() === 'tv') {
      return 'tv';
    }
    return 'default';
  } catch {
    // URLSearchParams can throw in exotic environments; fail safe.
    return 'default';
  }
}

/**
 * React hook: reads the display mode from the URL once at mount.
 * Returns a stable value for the lifetime of the page — TV displays
 * don't navigate, so reactivity on URL changes is not needed.
 */
export function useDisplayMode(): DisplayMode {
  // useState with lazy initializer: computed once on first render, never updated.
  const [mode] = useState<DisplayMode>(() =>
    typeof window !== 'undefined'
      ? parseDisplayMode(window.location.search)
      : 'default',
  );
  return mode;
}
